/** This is a generic search tile which are used on
 *  Last Minute and Tactical Always On ast he designs are same */

import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import cn from 'clsx'
import moment from 'moment'

import { mediaQueryUp } from 'components/utils/breakpoint'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import { OFFER_TYPE_LAST_MINUTE, OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import config from 'constants/config'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import OfferTileLoggedInButtons from '../../Support/OfferTileLoggedInButtons'
import OfferBadge from 'components/Common/OfferBadge'
import OfferBadgeTag from 'components/Common/OfferBadgeTag'
import LinePlaneFlyIcon from 'components/Luxkit/Icons/line/LinePlaneFlyIcon'
import OfferTilePricing from '../../Support/OfferTilePricing'
import OfferTileInclusions from '../../Support/OfferTileInclusions'
import SoldOutMessage from '../../Support/SoldOutMessage'
import GeoContext from 'contexts/geoContext'
import FormatCurrency from 'components/Common/FormatCurrency'
import { getSumExtraGuestsSurcharge } from 'lib/cartReservationUtils'
import shouldHideAoLabel from 'lib/hideAoLabel'
import useDefaultAirport from 'hooks/useDefaultAirport'
import { ImageParams } from 'components/Common/ResponsiveImage'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import CSSBreakpoint from 'components/Common/CSSBreakpoint'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import OfferUrgencyLabels from 'components/Common/Labels/OfferUrgencyLabels'
import OffsetBookmarkContainer from 'tripPlanner/components/Bookmark/Common/OffsetBookmarkContainer'
import ImageCarousel from 'components/Luxkit/Carousel/ImageCarousel'
import AspectRatio from 'components/Common/AspectRatio'
import { connect } from 'react-redux'
import { take } from 'lib/arrayUtils'
import TextButton from 'components/Luxkit/Button/TextButton'
import { getCustomerType } from 'lib/peopleLikeMeUtils'
import useHotelOfferSales from 'hooks/useHotelOfferSales'
import PeopleLikeMeSalesBanner from '../../../../SearchV2/Components/PeopleLikeMe/PeopleLikeMeSalesBanner'
import { showUserReviewsRating } from 'lib/reviewUtils'
import { encodeOfferIds } from 'components/Pages/HotelSearchPage/HotelSearchMapPage'
import { queryKeySelectedOfferIds } from 'constants/url'
import { setSearchParamValue } from 'lib/urlUtils'
import { getShowOnMapOfferExperimentVariant } from 'selectors/searchSelectors'
import { isUserReviewsAbTestEnabled } from 'selectors/featuresSelectors'
import SearchTileCancellationPolicy from 'components/SearchV2/SearchTileCancellationPolicy/SearchTileCancellationPolicy'
import { isDepositAvailableOnOffer, isDepositEnabledForOfferType } from 'lib/depositsUtils'
import FloatingBookmarkContainer from 'tripPlanner/components/Bookmark/Common/FloatingBookmarkContainer'
import OfferTilePropertyHeading from '.././OfferTilePropertyHeading'
import OfferTileLocationSection from '.././OfferTileLocationSection'
import ViewBundleOffer from '.././ViewBundleOffer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import SearchOfferTileInclusionsUpsellBanner from '../SearchOfferTileInclusionsUpsellBanner'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import useMinimumDurationPackageWithBonusInclusions from 'hooks/useMinimumDurationPackageWithBonusInclusions'

const Main = styled.div`
  position: relative;

  ${mediaQueryUp.md} {
    display: grid;
    grid-template-columns: 40% 1fr;
  }
`

const MainImageSegment = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  z-index: 0;

  ${mediaQueryUp.sm} {
    max-height: unset;
    grid-template-rows: 1fr;
  }
`

const DetailSegment = styled.div`
  padding: ${rem(20)} ${rem(24)};
  width: 100%;
  justify-content: space-between;
  position: relative;
  display: grid;
  grid-row-gap: ${rem(16)};
  grid-template-columns: 60% 1fr;
  grid-template-rows: auto min-content;
  min-height: ${rem(360)};
`

const LeftDetailSegment = styled.div`
  > * + * {
    margin-top: ${rem(16)}
  }
`

const RightDetailSegment = styled.div`
  align-self: flex-end;
  display: flex;
  flex-direction: column;
  margin-top: ${rem(16)};
  position: relative;
  min-height: ${rem(64)};
  width: fit-content;
  margin-left: ${rem(20)};
`

const PriceWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-content: flex-end;
  flex-direction: column;
  min-width: ${rem(180)};
  align-items: flex-start;
`

const StyledTileInclusions = styled(OfferTileInclusions)`
  margin-top: ${rem(8)};

  &.is-partner-property {
    max-height: ${rem(55)};
    overflow: hidden;
  }
`

const OfferOverlayContainer = styled.div`
  position: absolute;
  top: ${rem(54)};
  left: ${rem(20)};
  display: flex;
  align-items: flex-start;
  top: ${rem(12)};

  &.hide-type-label {
    top: ${rem(20)};
  }

  & > * + * {
    margin-top: ${rem(12)};
  }
`

const StyledOfferBadge = styled(OfferBadge)`
  position: absolute;
  top: ${rem(16)};
  right: ${rem(16)};
`

const OfferBadgeTagWrapper = styled.div`
  margin-top: ${rem(8)};
`

const AlignedLoader = styled(LoadingIndicator)`
  align-items: flex-start;
`

const HeaderActions = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
`

const UpsellWrapper = styled.div`
  grid-column: 1 / 3;
`

const imageParams: ImageParams = {
  quality: 'good',
  mdWidth: '768px',
}

interface Props {
  offer: App.Offer | App.OfferSummary;
  passedWalledGarden: boolean;
  productClick?: (offer: App.Offer | App.OfferSummary) => void;
  offerMetaData?: App.OfferListMetaData;
  filters?: App.OfferListFilters;
  bestPrice: App.OfferAvailableRate;
  soldOut: boolean;
  bestPricePackage: App.Package;
  eagerLoadFirstImage?: boolean;
  offerUrl: string;
  iOS: boolean;
  shouldShowSaleNumbers: boolean;
  windowSearch: string;
  shouldShowMapButton: boolean;
  isUserReviewsEnabled: boolean;
  offerAvailableRates: App.OfferAvailableRates;
  offerLinkIncludesFilters?: boolean;
}

function NewSearchOfferTileExpanded(props: Props) {
  const {
    offer,
    passedWalledGarden,
    filters,
    bestPrice,
    soldOut,
    bestPricePackage,
    eagerLoadFirstImage,
    offerUrl,
    iOS,
    shouldShowSaleNumbers,
    windowSearch,
    shouldShowMapButton,
    isUserReviewsEnabled,
    offerAvailableRates,
    offerMetaData,
    offerLinkIncludesFilters,
  } = props

  const {
    property,
    name,
    type,
    exclusiveExtrasLarge,
    tileDescription,
    tileInclusions,
    tileInclusionsHeading = 'Your handpicked inclusions',
    isPartnerProperty,
  } = offer

  const { currentCurrency, showTaxExclusivePrices } = useContext(GeoContext)

  const customerType = useMemo(() => getCustomerType(windowSearch), [windowSearch])
  const [count, loading] = useHotelOfferSales(offer?.id, customerType)

  const hideAoLabel = shouldHideAoLabel(offer)
  const { currentRegionHasFlights } = useContext(GeoContext)
  const defaultAirport = useDefaultAirport()

  const showBadge = !!offer.badge && config.BRAND === 'luxuryescapes'

  let inclusions: string | undefined
  let inclusionsList: any
  let inclusionsHeading: string

  // Will always be either a Last Minute Offer, or Tactial Always On
  if (offer.type === OFFER_TYPE_LAST_MINUTE) {
    inclusions = tileDescription ?? exclusiveExtrasLarge
    inclusionsHeading = tileInclusionsHeading ?? 'Exclusive extras:'
  } else {
    inclusions = tileDescription ?? offer.inclusions
    inclusionsHeading = tileInclusionsHeading ?? 'Handpicked inclusions:'
  }

  const flightPrice = offer.flightPrices[defaultAirport?.code]

  const totalPrice = useMemo(() => {
    if (!bestPricePackage) {
      return 0
    }
    return Math.ceil((bestPrice?.price ?? (bestPricePackage.price * (filters?.rooms?.length || 1))) + getSumExtraGuestsSurcharge(bestPricePackage, filters?.rooms ?? [], offer))
  }, [bestPricePackage, filters?.rooms, bestPrice, offer])

  const { searchTargetLabel, offerDistanceFromSearchTarget } = useContext(GlobalSearchStateContext)
  const searchTargetDistance = offerDistanceFromSearchTarget?.[offer.id]
  const showSearchDistance = !!searchTargetLabel && typeof searchTargetDistance === 'number' && searchTargetDistance > 0

  // This needs a memo because new moment objects aren't stable refs.
  const [checkInDate, selectedDuration] = useMemo(() => {
    const checkIn = filters?.checkIn ? moment(filters.checkIn) : undefined
    const checkOut = filters?.checkOut ? moment(filters.checkOut) : undefined
    const duration = checkIn && checkOut ? checkOut.diff(checkIn, 'days') : undefined

    return [checkIn, duration]
  }, [filters?.checkIn, filters?.checkOut])

  const minimumDurationPackageWithInclusions = useMinimumDurationPackageWithBonusInclusions(offer)
  const showPackageInclusionsUpsell = minimumDurationPackageWithInclusions && selectedDuration ? selectedDuration < minimumDurationPackageWithInclusions.duration : false
  const shouldShowInclusions = (!!inclusions && !!tileInclusions && tileInclusions.length > 0) && !showPackageInclusionsUpsell

  const location = [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', ')
  const cancellationPolicyType = bestPricePackage.roomRate?.cancellationPolicy?.type

  // This is temporary until we can improve memory on the offer listing page
  const images = iOS ? take(offer.images, 1, 0) : offer.images

  const priceMultiplier = bestPrice ? 1 : (filters?.rooms?.length || 1)
  const discountPercentage = bestPricePackage.discountPercent ? Math.round(bestPricePackage.discountPercent) : undefined

  const showSecureWithDepositTag = useMemo(() => isDepositEnabledForOfferType(offer.type) &&
  offer.packages.some((pkg) => {
    const propertyFees = pkg?.propertyFees ? pkg.propertyFees * priceMultiplier : 0
    const packagePrice = pkg?.price
    const total = showTaxExclusivePrices ? packagePrice : packagePrice + propertyFees
    const pkgAvailableRate = offerAvailableRates?.rates?.find(rate => rate.packageUniqueKey === pkg.uniqueKey)
    return isDepositAvailableOnOffer(offer, currentCurrency, checkInDate, total, pkg, pkgAvailableRate)
  }), [checkInDate, currentCurrency, offer, offerAvailableRates?.rates, priceMultiplier, showTaxExclusivePrices])

  const mapSearchUrl = `/search/map?${setSearchParamValue(windowSearch, queryKeySelectedOfferIds, encodeOfferIds([offer]))}`

  return (
    <Main className={cn({ 'is-partner-property': isPartnerProperty })}>
      <MainImageSegment>
        <AspectRatio xs="16:9" fillHeight>
          <ImageCarousel
            images={images}
            imageParams={imageParams}
            eagerLoadFirstImage={eagerLoadFirstImage}
          />
        </AspectRatio>
        {type === OFFER_TYPE_ALWAYS_ON &&
          <OfferOverlayContainer className={cn({ 'hide-type-label': hideAoLabel })}>
            <OfferPartnershipLabel offer={offer} />
          </OfferOverlayContainer>
        }
        {showBadge && offer.badge && <StyledOfferBadge badge={offer.badge} dimensions={{ width: 86, height: 86 }} />}
      </MainImageSegment>
      <div>
        { shouldShowSaleNumbers && !loading && <PeopleLikeMeSalesBanner customerType={customerType} saleCount={count} />}
        <DetailSegment>
          <TripGuard>
            <FloatingBookmarkContainer>
              <BookmarkButton offer={offer} />
            </FloatingBookmarkContainer>
            <CSSBreakpoint max="sm">
              <OffsetBookmarkContainer>
                <BookmarkButton offer={offer} />
              </OffsetBookmarkContainer>
            </CSSBreakpoint>
          </TripGuard>
          <LeftDetailSegment>
            <TripGuard>
              <CSSBreakpoint min="md">
                <HeaderActions>
                  {isUserReviewsEnabled && showUserReviewsRating(offer.property?.rating, offer.property?.reviewsTotal) && (
                    <NumberRating
                      variant="medium"
                      rating={offer.property.rating}
                      total={offer.property.reviewsTotal}
                      ratingSubtitle={offer.property.reviewsSource === 'google' && 'From online reviews'}
                    />
                  )}
                </HeaderActions>
              </CSSBreakpoint>
            </TripGuard>
            <OfferTileLocationSection
              location={location}
              showSearchDistance={showSearchDistance}
              shouldShowMapLink={shouldShowMapButton}
              mapSearchUrl={mapSearchUrl}
              searchTargetDistance={searchTargetDistance}
              searchTargetLabel={searchTargetLabel}
            />
            {!hideAoLabel && <ProductTypeLabel productType={offer.productType} />}
            <OfferUrgencyLabels offer={offer} />
            <div>
              <OfferTilePropertyHeading name={property.name} descriptiveName={name} />
              {showBadge && <OfferBadgeTagWrapper>
                <OfferBadgeTag badge={offer.badge} />
              </OfferBadgeTagWrapper>}
            </div>

            {isUserReviewsEnabled && showUserReviewsRating(offer.property?.rating, offer.property?.reviewsTotal) && (
              <CSSBreakpoint max="sm">
                <NumberRating
                  inlineLabel variant="xs"
                  rating={offer.property.rating}
                  total={offer.property.reviewsTotal}
                  ratingSubtitle={offer.property.reviewsSource === 'google' && 'From online reviews'}
                />
              </CSSBreakpoint>
            )}
            <SearchTileCancellationPolicy
              cancellationPolicyType={cancellationPolicyType}
              checkInDate={checkInDate}
              timezoneOffset={offer?.property?.timezoneOffset}
            />
            {shouldShowInclusions &&
              <StyledTileInclusions
                className={cn({ 'is-partner-property': isPartnerProperty })}
                content={inclusions}
                tileInclusionsList={inclusionsList}
                heading={inclusionsHeading}
                maxInclusionsCount={3}
              />
            }
          </LeftDetailSegment>
          <RightDetailSegment>
            <PriceWrapper>
              <VerticalSpacer gap={16}>
                {bestPricePackage &&
                  <OfferTileLoggedInButtons
                    signUpText="Sign up for free"
                    discountPercentage={discountPercentage}
                    align="left"
                  >
                    <OfferTilePricing
                      offer={offer}
                      rooms={filters?.rooms}
                      hasDates={!!filters?.checkIn}
                      checkInDate={checkInDate}
                      pkg={bestPricePackage}
                      pricing={bestPrice}
                      offerUrl={offerUrl}
                      align="left"
                      flexibleMonths={filters?.flexibleMonths}
                      flexibleNights={filters?.flexibleNights}
                      hasPackageWithDepositsEnabled={showSecureWithDepositTag}
                    />
                    <TextButton fit="full-width" kind="primary">View offer</TextButton>
                  </OfferTileLoggedInButtons>}
              </VerticalSpacer>
            </PriceWrapper>

            {soldOut && passedWalledGarden && <SoldOutMessage offer={offer} filters={filters} />}

            {currentRegionHasFlights && offer.offerFlightsEnabled &&
              <CaptionTextBlock variant="medium" startIcon={<LinePlaneFlyIcon colour="primary" />}>
                {!filters?.checkIn && !!flightPrice && passedWalledGarden && <>Hotel + flight package from <FormatCurrency value={flightPrice + totalPrice} /> (2 adults)</>}
                {(filters?.checkIn || !flightPrice || !passedWalledGarden) && 'Hotel + flight package available'}
              </CaptionTextBlock>
          }
            {!bestPricePackage && passedWalledGarden && <AlignedLoader floating opaque />}
          </RightDetailSegment>
          {config.ENABLE_BUNDLE_AND_SAVE && offerMetaData?.bundleOfferId && (
            <UpsellWrapper>
              <ViewBundleOffer offer={offer} filters={filters} offerMetaData={offerMetaData} offerLinkIncludesFilters={offerLinkIncludesFilters} />
            </UpsellWrapper>
          )}
          {!offerMetaData?.bundleOfferId && showPackageInclusionsUpsell &&
            <UpsellWrapper>
              <SearchOfferTileInclusionsUpsellBanner offer={offer} filters={filters} newTile />
            </UpsellWrapper>}
        </DetailSegment>
      </div>
    </Main>
  )
}

NewSearchOfferTileExpanded.defaultProps = {
  filters: {},
}

const mapStateToProps = (state: App.State, ownProps: { offer: App.Offer | App.OfferSummary }) => ({
  iOS: state.config.userAgent.osName === 'iOS',
  shouldShowSaleNumbers: state.router.location.pathname.endsWith('people-like-me'),
  windowSearch: state.router.location.search,
  shouldShowMapButton: getShowOnMapOfferExperimentVariant(state),
  isUserReviewsEnabled: isUserReviewsAbTestEnabled(state),
  offerAvailableRates: state.offer.offerAvailableRates[ownProps.offer.id],
})

export default connect(mapStateToProps)(NewSearchOfferTileExpanded)
